import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

import { SousService } from 'app/models/sous-service';
import { ServicesService } from 'app/services/services/services.service';
import { SousServiceService } from 'app/services/sous-Service/sous-service.service';
import { AwscontrolService, UploadResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';

type ImageUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-ajouter-sous-service',
  templateUrl: './ajouter-sous-service.component.html',
  styleUrls: ['./ajouter-sous-service.component.scss']
})
export class AjouterSousServiceComponent implements OnInit {
  // Sous-service data
  sousService: SousService = new SousService();
  service: any; // Parent service
  idService: string | null = null;
  agencyId: string | null = null;

  // Langues de l'agence
  agencyLanguages: string[] = ['FR'];

  // ==================== MAIN IMAGE ====================
  mainImage: string | null = null;
  mainImageFile: File | null = null;
  isUploadingMain: boolean = false;

  mainImageUploadMode: ImageUploadMode = 'direct';
  mainPreviewOriginalUrl: string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;

  // ==================== GALLERY ====================
  galleryImages: string[] = [];
  selectedGalleryFiles: FileList | null = null;
  isUploadingGallery: boolean = false;
  galleryUploadProgress: number = 0;

  galleryUploadMode: ImageUploadMode = 'direct';
  galleryPreviewOriginalUrls: string[] = [];
  galleryPreviewProcessedUrls: string[] = [];
  isAwaitingGalleryValidation: boolean = false;

  // Arabic fields
  namearabe: string = '';
  categoryarab: string = '';
  descriptionarabe: string = '';

  // English fields
  nameanglais: string = '';
  categoryanglais: string = '';
  descriptionanglais: string = '';

  // Video URL
  video: string = '';

  // Form state
  isSubmitting: boolean = false;

  @ViewChild('mainImageInput') mainImageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('galleryFileInput') galleryFileInput!: ElementRef<HTMLInputElement>;

  // ============================================================
  // ✅ RÉFÉRENCES AUX ÉDITEURS contenteditable (WYSIWYG)
  // ============================================================
  @ViewChild('descFrEditor') descFrEditor?: ElementRef<HTMLElement>;
  @ViewChild('descArEditor') descArEditor?: ElementRef<HTMLElement>;
  @ViewChild('descEnEditor') descEnEditor?: ElementRef<HTMLElement>;
  // ============================================================

  constructor(
    private serviceService: ServicesService,
    private sousServiceService: SousServiceService,
    private awsService: AwscontrolService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.idService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadServiceDetails();
    this.serviceService.refreshNedeed.subscribe(() => this.loadServiceDetails());
  }

  loadServiceDetails(): void {
    if (this.idService) {
      this.serviceService.getService(this.idService).subscribe({
        next: (data: any) => {
          this.service = data;
          if (data.user?.id) {
            this.agencyId = data.user.id.toString();
          } else if (data.userId) {
            this.agencyId = data.userId.toString();
          } else if (data.agencyId) {
            this.agencyId = data.agencyId.toString();
          }

          if (data.user?.availableLanguages && Array.isArray(data.user.availableLanguages)) {
            this.agencyLanguages = data.user.availableLanguages;
          } else {
            this.agencyLanguages = ['FR'];
          }

          console.log('Service loaded:', this.service, 'Agency ID:', this.agencyId, 'Languages:', this.agencyLanguages);

          // Re-sync les éditeurs après chargement
          this.applyRichTextAfterRender();
        },
        error: (error) => {
          console.error('Error loading service:', error);
          Swal.fire('Erreur', 'Impossible de charger les détails du service', 'error');
        }
      });
    }
  }

  /* ============================================================
     ✅ SYNC DES ÉDITEURS contenteditable
     ============================================================ */
  private applyRichTextAfterRender(): void {
    setTimeout(() => this.syncEditors(), 0);
    setTimeout(() => this.syncEditors(), 100);
    setTimeout(() => this.syncEditors(), 300);
  }

  private syncEditors(): void {
    if (this.descFrEditor) {
      const html = this.sousService.description || '';
      if (this.descFrEditor.nativeElement.innerHTML !== html) {
        this.descFrEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descArEditor) {
      const html = this.descriptionarabe || '';
      if (this.descArEditor.nativeElement.innerHTML !== html) {
        this.descArEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descEnEditor) {
      const html = this.descriptionanglais || '';
      if (this.descEnEditor.nativeElement.innerHTML !== html) {
        this.descEnEditor.nativeElement.innerHTML = html;
      }
    }
  }

  /* ============================================================
     ✅ HANDLERS (input) DES CONTENTEDITABLE
     ============================================================ */
  onDescFrInput(): void {
    if (this.descFrEditor) {
      this.sousService.description = this.descFrEditor.nativeElement.innerHTML;
    }
  }

  onDescArInput(): void {
    if (this.descArEditor) {
      this.descriptionarabe = this.descArEditor.nativeElement.innerHTML;
    }
  }

  onDescEnInput(): void {
    if (this.descEnEditor) {
      this.descriptionanglais = this.descEnEditor.nativeElement.innerHTML;
    }
  }

  /* ============================================================
     BOLD — seule action de rich text exposée
     ============================================================ */
  makeBold(): void {
    try {
      document.execCommand('bold', false);
    } catch (e) {
      console.warn('execCommand("bold") failed:', e);
    }
  }

  // ==================== VALIDATION UTILITAIRE ====================
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire('Format non supporté', 'Veuillez sélectionner une image (JPEG, PNG, JPG, GIF, WEBP)', 'warning');
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire('Fichier trop volumineux', 'La taille maximale est de 5MB', 'warning');
      return false;
    }
    return true;
  }

  // ============================================================
  // ==================== MAIN IMAGE ============================
  // ============================================================

  setMainUploadMode(mode: ImageUploadMode): void {
    if (this.isUploadingMain) return;
    if (this.isAwaitingMainValidation) {
      this.cancelMainImagePreview(true);
    }
    this.mainImageUploadMode = mode;
    console.log('🔄 Mode image principale :', mode);
  }

  triggerMainImageUpload(): void {
    if (!this.isUploadingMain && !this.isAwaitingMainValidation) {
      this.mainImageInput.nativeElement.click();
    }
  }

  onMainImageSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files && target.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) {
      target.value = '';
      return;
    }
    this.mainImageFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.mainPreviewOriginalUrl = (e.target?.result as string) || null;
    };
    reader.readAsDataURL(file);

    if (this.mainImageUploadMode === 'direct') {
      this.uploadMainImage();
    } else {
      this.uploadMainImageViaImagesousproduit();
    }
  }

  uploadMainImage(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMain = true;

    this.awsService.uploadFile(this.mainImageFile, 'sous-service-main-images').subscribe({
      next: (response: UploadResponse) => {
        this.isUploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainImage = response.urls[0];
          this.mainPreviewOriginalUrl = null;
          Swal.fire({
            icon: 'success',
            title: 'Image principale téléchargée',
            text: 'L\'image de couverture a été ajoutée',
            timer: 1500,
            showConfirmButton: false
          });
          if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
          this.mainImageFile = null;
        } else {
          Swal.fire('Erreur', response.error || 'Échec du téléchargement', 'error');
        }
      },
      error: (err) => {
        this.isUploadingMain = false;
        console.error('Main image upload error:', err);
        Swal.fire('Erreur', 'Impossible de télécharger l\'image principale', 'error');
        this.mainPreviewOriginalUrl = null;
      }
    });
  }

  uploadMainImageViaImagesousproduit(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMain = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imagesousproduit([this.mainImageFile], 'sous-service-main-images').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainPreviewProcessedUrl = response.urls[0];
          this.isAwaitingMainValidation = true;
        } else {
          Swal.fire('Erreur', (response.errors && response.errors[0]) || 'Échec du traitement', 'error');
          this.mainPreviewOriginalUrl = null;
        }
      },
      error: () => {
        this.isUploadingMain = false;
        Swal.fire('Erreur', 'Impossible de traiter l\'image principale', 'error');
        this.mainPreviewOriginalUrl = null;
      }
    });
  }

  validateMainImagePreview(): void {
    if (!this.mainPreviewProcessedUrl) return;

    this.mainImage = this.mainPreviewProcessedUrl;

    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;
    this.mainImageFile = null;

    if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';

    Swal.fire({
      icon: 'success',
      title: 'Image validée',
      text: 'L\'image principale a été acceptée.',
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelMainImagePreview(silent: boolean = false): void {
    const urlToDelete = this.mainPreviewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image principale refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
      this.mainImageFile = null;

      if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser cette image ?',
      text: 'L\'image traitée sera supprimée et vous pourrez en choisir une autre.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  removeMainImage(): void {
    if (this.isAwaitingMainValidation) {
      this.cancelMainImagePreview();
      return;
    }
    if (!this.mainImage) return;

    Swal.fire({
      title: 'Supprimer l\'image principale ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, supprimer'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(this.mainImage!).toPromise();
          this.mainImage = null;
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ============================================================
  // ==================== GALERIE ===============================
  // ============================================================

  setGalleryUploadMode(mode: ImageUploadMode): void {
    if (this.isUploadingGallery) return;
    if (this.isAwaitingGalleryValidation) {
      this.cancelGalleryPreview(true);
    }
    this.galleryUploadMode = mode;
    console.log('🔄 Mode galerie :', mode);
  }

  triggerGalleryUpload(): void {
    if (!this.isUploadingGallery && !this.isAwaitingGalleryValidation) {
      this.galleryFileInput.nativeElement.click();
    }
  }

  onGalleryFilesSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (this.validateImageFile(files[i])) {
        validFiles.push(files[i]);
      }
    }

    if (validFiles.length === 0) {
      target.value = '';
      return;
    }

    this.selectedGalleryFiles = target.files;

    this.galleryPreviewOriginalUrls = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = (e.target?.result as string) || '';
        if (url) this.galleryPreviewOriginalUrls.push(url);
      };
      reader.readAsDataURL(file);
    });

    if (this.galleryUploadMode === 'direct') {
      this.uploadGalleryImages(validFiles);
    } else {
      this.uploadGalleryImagesViaImagesousproduit(validFiles);
    }

    target.value = '';
  }

  uploadGalleryImages(files: File[]): void {
    if (!files || files.length === 0) return;
    this.isUploadingGallery = true;
    this.galleryUploadProgress = 0;

    Swal.fire({
      title: 'Téléchargement en cours...',
      html: '0%',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    this.awsService.uploadFiles(files, 'sous-service-images').subscribe({
      next: (response: UploadResponse) => {
        this.isUploadingGallery = false;
        Swal.close();
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryImages.push(...response.urls);
          this.galleryPreviewOriginalUrls = [];
          Swal.fire({
            icon: 'success',
            title: 'Images téléchargées',
            text: `${response.urls.length} image(s) ajoutée(s) à la galerie`,
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire('Erreur', response.error || 'Échec du téléchargement', 'error');
          this.galleryPreviewOriginalUrls = [];
        }
      },
      error: (err) => {
        this.isUploadingGallery = false;
        Swal.close();
        console.error('Gallery upload error:', err);
        Swal.fire('Erreur', 'Impossible de télécharger les images de la galerie', 'error');
        this.galleryPreviewOriginalUrls = [];
      }
    });
  }

  uploadGalleryImagesViaImagesousproduit(files: File[]): void {
    if (!files || files.length === 0) return;
    this.isUploadingGallery = true;
    this.isAwaitingGalleryValidation = false;
    this.galleryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, 'sous-service-images').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploadingGallery = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryPreviewProcessedUrls = response.urls;
          this.isAwaitingGalleryValidation = true;
        } else {
          Swal.fire('Erreur', (response.errors && response.errors[0]) || 'Échec du traitement', 'error');
          this.galleryPreviewOriginalUrls = [];
        }
      },
      error: () => {
        this.isUploadingGallery = false;
        Swal.fire('Erreur', 'Impossible de traiter les images de la galerie', 'error');
        this.galleryPreviewOriginalUrls = [];
      }
    });
  }

  validateGalleryPreview(): void {
    if (!this.galleryPreviewProcessedUrls.length) return;

    this.galleryImages.push(...this.galleryPreviewProcessedUrls);

    this.galleryPreviewOriginalUrls = [];
    this.galleryPreviewProcessedUrls = [];
    this.isAwaitingGalleryValidation = false;
    this.selectedGalleryFiles = null;

    if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';

    Swal.fire({
      icon: 'success',
      title: 'Images validées',
      text: `${this.galleryImages.length} image(s) dans la galerie.`,
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelGalleryPreview(silent: boolean = false): void {
    const urlsToDelete = [...this.galleryPreviewProcessedUrls];

    const doCancel = () => {
      urlsToDelete.forEach(url => {
        this.awsService.deleteFile(url).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      });
      this.galleryPreviewOriginalUrls = [];
      this.galleryPreviewProcessedUrls = [];
      this.isAwaitingGalleryValidation = false;
      this.selectedGalleryFiles = null;

      if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser ces images ?',
      text: 'Les images traitées seront supprimées et vous pourrez en choisir d\'autres.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  deleteGalleryImage(index: number): void {
    const imageUrl = this.galleryImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, supprimer'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(imageUrl).toPromise();
          this.galleryImages.splice(index, 1);
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ==================== FORM VALIDATION ====================
  validateForm(): boolean {
    if (this.isAwaitingMainValidation) {
      Swal.fire('Validation requise', 'Veuillez valider ou refuser l\'image principale.', 'warning');
      return false;
    }
    if (this.isAwaitingGalleryValidation) {
      Swal.fire('Validation requise', 'Veuillez valider ou refuser les images de la galerie.', 'warning');
      return false;
    }
    if (this.isUploadingMain || this.isUploadingGallery) {
      Swal.fire('Téléchargement en cours', 'Veuillez attendre la fin des téléchargements', 'warning');
      return false;
    }
    if (!this.sousService.name || this.sousService.name.trim() === '') {
      Swal.fire('Nom requis', 'Veuillez saisir un nom', 'warning');
      return false;
    }
    if (!this.sousService.description || this.sousService.description.trim() === '') {
      Swal.fire('Description requise', 'Veuillez saisir une description', 'warning');
      return false;
    }
    if (!this.mainImage) {
      Swal.fire('Image principale requise', 'Veuillez ajouter une image de couverture', 'warning');
      return false;
    }
    return true;
  }

  // ==================== SUBMIT ====================
  onSubmit(): void {
    if (!this.validateForm()) return;
    if (!this.idService) {
      Swal.fire('Erreur', 'ID service manquant', 'error');
      return;
    }

    // 🔥 Récupérer les valeurs depuis les éditeurs contenteditable
    this.onDescFrInput();
    this.onDescArInput();
    this.onDescEnInput();

    this.isSubmitting = true;
    Swal.fire({
      title: 'Création en cours...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    const sousServiceData = {
      name: this.sousService.name.trim(),
      namearabe: this.namearabe?.trim() || '',
      nameanglais: this.nameanglais?.trim() || '',
      category: this.sousService.category?.trim() || '',
      categoryarab: this.categoryarab?.trim() || '',
      categoryanglais: this.categoryanglais?.trim() || '',
      description: this.sousService.description,
      descriptionarabe: this.descriptionarabe?.trim() || '',
      descriptionanglais: this.descriptionanglais?.trim() || '',
      prix: this.sousService.prix?.trim() || '',
      video: this.video?.trim() || '',
      image_url: this.mainImage,
      listimages: this.galleryImages
    };

    this.sousServiceService.createSousService(sousServiceData, this.idService).subscribe({
      next: () => {
        Swal.close();
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès !',
          text: 'Sous-service créé avec succès',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          this.router.navigate(['/list-Sous-Services', this.idService]);
        });
      },
      error: (error: HttpErrorResponse) => {
        Swal.close();
        this.isSubmitting = false;
        let errorMessage = 'Erreur lors de la création';
        if (error.status === 400) errorMessage = 'Données invalides. Vérifiez les champs.';
        else if (error.status === 401) errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        else if (error.status === 403) errorMessage = 'Permissions insuffisantes.';
        else if (error.status === 500) errorMessage = 'Erreur serveur. Réessayez plus tard.';
        Swal.fire('Erreur', errorMessage, 'error');
      }
    });
  }

  // ==================== CANCEL / RESET ====================
  cancel(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-Services', this.agencyId]);
    } else {
      this.router.navigate(['listeServices']);
    }
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser le formulaire ?',
      text: 'Toutes les données non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.sousService = new SousService();
        this.namearabe = '';
        this.categoryarab = '';
        this.descriptionarabe = '';
        this.nameanglais = '';
        this.categoryanglais = '';
        this.descriptionanglais = '';
        this.video = '';
        this.mainImage = null;
        this.galleryImages = [];
        this.mainImageFile = null;
        this.selectedGalleryFiles = null;

        this.mainPreviewOriginalUrl = null;
        this.mainPreviewProcessedUrl = null;
        this.isAwaitingMainValidation = false;
        this.mainImageUploadMode = 'direct';

        this.galleryPreviewOriginalUrls = [];
        this.galleryPreviewProcessedUrls = [];
        this.isAwaitingGalleryValidation = false;
        this.galleryUploadMode = 'direct';

        if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
        if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';

        Swal.fire('Formulaire réinitialisé', '', 'success');
        this.applyRichTextAfterRender();
      }
    });
  }
}